import {observer} from "mobx-react-lite";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {IconSource} from "../../../shared/enums/iconSource";
import EventBooking from "../../../stores/domainStore/pages/models/eventBooking";
import {useStores} from "../../../stores/useStores";
import ButtonIcon from "../../atoms/button/buttonIcon";
import "./multiSelectBookingOptions.sass";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import NavigationButton from "../../atoms/button/navigationButton";
import {EventBookingStatus} from "../../../shared/enums/eventBookingStatus";

export interface IMultiSelectBookingOptionsProps {
  onRender: () => void;
  onUnmount: () => void;
}

const MultiSelectBookingOptions: React.FC<IMultiSelectBookingOptionsProps> = ({
  onRender,
  onUnmount
}) => {
  const {t} = useTranslation(["translation", "event"]);
  const {
    uiStore: {eventsUiStore},
    domainStore: {eventsDomainStore}
  } = useStores();

  useEffect(() => {
    onRender();
    return () => {
      onUnmount();
    };
  }, []);

  const event = eventsDomainStore?.editableEvent;
  const selectedCount = eventsUiStore.selectedBookingIds.size;
  const capacityRemaining = event?.bookingSpacesDetails.capacityRemaining || 0;
  const hasEnoughCapacity =
    event?.bookingSpacesDetails?.isUnlimitedCapacity ||
    capacityRemaining >= selectedCount;

  const selectedBookingsAreNotConfirmed =
    !eventsUiStore.confirmedEventBookings.some((w: EventBooking) =>
      eventsUiStore.selectedBookingIds.has(w.id)
    );

  const canConfirmBookings =
    selectedBookingsAreNotConfirmed && hasEnoughCapacity;

  const canDeleteBookings = !eventsUiStore.cancelledEventBookings.some(
    (w: EventBooking) => eventsUiStore.selectedBookingIds.has(w.id)
  );

  const [isOpen, setIsOpen] = useState(false);
  const isBulkEditBookingsDisabled =
    eventsUiStore.isLoadingEventBookings || eventsUiStore.hasUnsavedBookings;

  return (
    <div className="multi-select-booking-options">
      <div>
        <span>
          {t("event:labels.bookingsSelected", {
            count: selectedCount
          })}
        </span>
        <ButtonIcon
          type="secondary"
          onClick={() => eventsUiStore.clearSelectedBookings()}
        >
          {t("event:actions.clear")}
        </ButtonIcon>
      </div>
      <div>
        <ButtonIcon
          type="primary"
          icon={IconSource.successTick}
          isBusy={eventsUiStore.isConfirmingBookings}
          isDisabled={!canConfirmBookings || isBulkEditBookingsDisabled}
          onClick={() =>
            eventsUiStore.updateSelectedBookings(EventBookingStatus.Confirmed)
          }
        >
          {t("event:actions.confirmBookings")}
        </ButtonIcon>
        <ButtonIcon
          type="secondary-negative"
          icon={IconSource.exit}
          onClick={() => setIsOpen(true)}
          isDisabled={!canDeleteBookings || isBulkEditBookingsDisabled}
        >
          {t("event:actions.deleteBookings")}
        </ButtonIcon>
      </div>
      <Modal
        isOpen={isOpen}
        modalClassName="multi-select-booking-options-modal"
        contentClassName="border"
        centered
        data-testid="eventTemplateDetailModal"
      >
        <ModalHeader
          close={
            <NavigationButton
              icon={IconSource.exit}
              onClick={() => setIsOpen(false)}
            />
          }
        >
          <div>
            {t("event:titles.deleteBookings", {
              count: eventsUiStore.selectedBookingIds.size
            })}
          </div>
        </ModalHeader>
        <ModalBody>
          {t(
            eventsUiStore.selectedBookingsIncludesPaidBooking
              ? "event:messages.confirmDeleteMultiplePaidBookings"
              : "event:messages.confirmDeleteMultipleBookings",
            {
              count: eventsUiStore.selectedBookingIds.size
            }
          )}
        </ModalBody>
        <ModalFooter>
          <div>
            <ButtonIcon type="secondary" onClick={() => setIsOpen(false)}>
              {t("translation:confirmations.negative")}
            </ButtonIcon>
          </div>
          <ButtonIcon
            type="primary-negative"
            icon={IconSource.exit}
            isBusy={eventsUiStore.isDeletingBookings}
            onClick={() =>
              eventsUiStore
                .updateSelectedBookings(EventBookingStatus.Cancelled)
                .finally(() => {
                  setIsOpen(false);
                })
            }
          >
            {t("event:actions.deleteBookings")}
          </ButtonIcon>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default observer(MultiSelectBookingOptions);
